<template>
  <div class="view">
    <h1 v-underline-primary>{{ $t('explore.title') }}</h1>

    <div ref="tabs" class="tabs" role="group" :aria-label="$t('explore.views')">
      <button v-for="tab in tabs" :key="tab" :class="{ active: activeTab === tab }" :aria-pressed="activeTab === tab ? 'true' : 'false'" @click="setActiveTab(tab)">{{ $t('explore.tabs.' + tab) }}</button>
    </div>

    <!-- Feed -->
    <template v-if="activeTab === 'feed'">
      <section class="report-section" aria-labelledby="top-rated">
        <h2 id="top-rated">{{ $t('explore.topRated') }}</h2>
        <ul class="report-list">
          <li v-for="report in topRatedReports" :key="report.submission_id"><ReportCard :report="report" /></li>
        </ul>
      </section>
      <section class="report-section" aria-labelledby="most-recent">
        <h2 id="most-recent">{{ $t('explore.mostRecent') }}</h2>
        <ul class="report-list">
          <li v-for="report in recent" :key="report.submission_id"><ReportCard :report="report" /></li>
        </ul>
        <button v-if="recentMore" type="button" class="btn-standard load-more" :disabled="busy" @click="loadMoreRecent">{{ $t('explore.loadMore') }}</button>
      </section>
    </template>

    <!-- Map -->
    <section v-else-if="activeTab === 'map'" class="report-section" aria-labelledby="map-h">
      <h2 id="map-h" class="sr-only">{{ $t('explore.tabs.map') }}</h2>
      <CityMap v-if="points.length" :points="points" tall popup />
      <p v-else-if="pointsLoaded" class="muted">{{ $t('explore.mapEmpty') }}</p>
      <p class="map-legend"><span class="d sev1"></span>{{ $t('city.severity.1') }} <span class="d sev2"></span>{{ $t('city.severity.2') }} <span class="d sev3"></span>{{ $t('city.severity.3') }}</p>
    </section>

    <!-- Drill-in: reports of one city or country (shared by both tabs) -->
    <section v-else-if="drill" class="report-section" aria-labelledby="drill-title">
      <button class="back-button" @click="clearSelection">
        <i class="fas fa-arrow-left" aria-hidden="true"></i> {{ $t('common.actions.back') }}
      </button>
      <h2 id="drill-title" v-underline-primary>{{ drill.title }}</h2>
      <ul class="report-list">
        <li v-for="report in drill.reports" :key="report.submission_id"><ReportCard :report="report" detailed /></li>
      </ul>
      <button v-if="drillMore" type="button" class="btn-standard load-more" :disabled="busy" @click="loadMoreDrill">{{ $t('explore.loadMore') }}</button>
    </section>

    <!-- City: ranked rows -->
    <template v-else-if="activeTab === 'city'">
    <div class="search">
      <label class="sr-only" for="city-search">{{ $t('explore.search.cityLabel') }}</label>
      <i class="fas fa-search" aria-hidden="true"></i>
      <input id="city-search" v-model.trim="query" type="search" :placeholder="$t('explore.search.city')" autocomplete="off" />
    </div>
    <div class="stats">
      <div class="stat"><span class="stat-value">{{ cityRows.length }}</span><span class="stat-label">{{ $t('explore.stats.cities') }}</span></div>
      <div class="stat"><span class="stat-value">{{ total }}</span><span class="stat-label">{{ $t('explore.stats.reports') }}</span></div>
    </div>
    <ol v-if="visibleCityRows.length" class="rank-list">
      <li v-for="row in visibleCityRows" :key="row.name">
        <button type="button" class="rank-row" @click="selectCity(row.name)">
          <span class="rank" aria-hidden="true">{{ String(row.rank).padStart(2, '0') }}</span>
          <span class="rank-main">
            <span class="rank-name">{{ row.name === 'Other' ? $t('explore.other') : row.name }}</span>
            <span class="rank-sub">{{ $t('explore.highCount', row.high) }}</span>
            <span class="bar" aria-hidden="true"><span class="bar-fill" :style="{ width: row.pct + '%' }"></span></span>
          </span>
          <span class="rank-count">{{ row.count }}<span class="sr-only"> {{ $t('explore.reportCount', row.count) }}</span></span>
          <i class="fas fa-chevron-right chevron" aria-hidden="true"></i>
        </button>
      </li>
    </ol>
    <p v-else class="empty" role="status">{{ $t('explore.noResults') }}</p>

    </template>

    <!-- Country: featured tile + grid -->
    <template v-else-if="activeTab === 'country'">
    <div class="search">
      <label class="sr-only" for="country-search">{{ $t('explore.search.countryLabel') }}</label>
      <i class="fas fa-search" aria-hidden="true"></i>
      <input id="country-search" v-model.trim="query" type="search" :placeholder="$t('explore.search.country')" autocomplete="off" />
    </div>
    <template v-if="visibleCountryRows.length">
      <button v-if="featuredCountry" type="button" class="featured" @click="selectCountry(featuredCountry.name)">
        <span class="featured-card">
          <span class="eyebrow">{{ $t('explore.mostReports') }}</span>
          <span class="kpi-head"><span class="featured-count">{{ featuredCountry.count }}</span><span class="kpi-unit" aria-hidden="true">{{ $t('explore.reportsUnit') }}</span></span>
          <span class="featured-name">{{ featuredCountry.name }}</span>
          <span class="sr-only">{{ kpiLabel(featuredCountry) }}</span>
          <span class="kpi-viz" aria-hidden="true">
            <span class="mix">
              <span v-for="seg in mixSegments(featuredCountry)" :key="seg.level" :class="['mix-seg', 'mix-' + seg.level]" :style="{ flexGrow: seg.n }"></span>
            </span>
            <span class="mix-legend"><span v-for="seg in mixSegments(featuredCountry)" :key="seg.level" class="mix-key"><span :class="['sym', 'sym-' + seg.level]"></span>{{ seg.n }}</span></span>
          </span>
          <span v-if="featuredCountry.topLabel" class="issue-tag">{{ $label(featuredCountry.topLabel) }}</span>
        </span>
      </button>
      <ul v-if="gridCountries.length" class="tile-grid">
        <li v-for="row in gridCountries" :key="row.name">
          <button type="button" class="tile" @click="selectCountry(row.name)">
            <span class="kpi-head"><span class="tile-count">{{ row.count }}</span><span class="kpi-unit" aria-hidden="true">{{ $t('explore.reportsUnit') }}</span></span>
            <span class="tile-name">{{ row.name }}</span>
            <span class="sr-only">{{ kpiLabel(row) }}</span>
            <span class="kpi-viz" aria-hidden="true">
              <span class="mix">
                <span v-for="seg in mixSegments(row)" :key="seg.level" :class="['mix-seg', 'mix-' + seg.level]" :style="{ flexGrow: seg.n }"></span>
              </span>
              <span class="mix-legend"><span v-for="seg in mixSegments(row)" :key="seg.level" class="mix-key"><span :class="['sym', 'sym-' + seg.level]"></span>{{ seg.n }}</span></span>
            </span>
            <span v-if="row.topLabel" class="issue-tag">{{ $label(row.topLabel) }}</span>
          </button>
        </li>
      </ul>
    </template>
    <p v-else class="empty" role="status">{{ $t('explore.noResults') }}</p>

    </template>
  </div>
</template>

<script>
import CityMap from '@/components/CityMap.vue';
import ReportCard from '@/components/ReportCard.vue';
import { placeIndicator, observeIndicator } from '@/utils/slidingIndicator';
import { exploreData } from '@/utils/exploreApi';

export default {
  components: { ReportCard, CityMap },
  name: "ExploreFeed",
  data() {
    return {
      activeTab: "feed",
      tabs: ['feed', 'city', 'country', 'map'],
      // Everything comes pre-reduced from /data/explore (see utils/exploreApi.js): no full report list in the browser
      total: 0,
      topRatedReports: [],
      recent: [],
      recentPage: 1,
      recentMore: false,
      cityRows: [],
      countryRows: [],
      points: [],
      pointsLoaded: false,
      drillReports: [],
      drillPage: 1,
      drillMore: false,
      busy: false,
      selectedCity: null,
      selectedCountry: null,
      query: '',
    };
  },
  created() {
    this.activeTab = this.$route.query.tab || "feed";
    this.loadSummary();
    if (this.activeTab === 'map') this.loadPoints();
  },
  computed: {
    // The selected city or country, as one shape for the shared drill-in view
    drill() {
      if (this.activeTab === 'city' && this.selectedCity) {
        return { title: this.selectedCity === 'Other' ? this.$t('explore.other') : this.selectedCity, reports: this.drillReports };
      }
      if (this.activeTab === 'country' && this.selectedCountry) {
        return { title: this.selectedCountry === 'Other' ? this.$t('explore.other') : this.selectedCountry, reports: this.drillReports };
      }
      return null;
    },
    visibleCityRows() { return this.filterRows(this.cityRows); },
    visibleCountryRows() { return this.filterRows(this.countryRows); },
    featuredCountry() { return this.visibleCountryRows[0] || null; },
    gridCountries() { return this.visibleCountryRows.slice(1); }
  },

  mounted() {
    this.$nextTick(this.placeTab);
    this.tabsObserver = observeIndicator(this.$refs.tabs, '.tabs button.active');
  },
  beforeUnmount() {
    if (this.tabsObserver) this.tabsObserver.disconnect();
  },
  watch: {
    activeTab(t) { this.$nextTick(this.placeTab); if (t === 'map') this.loadPoints(); },
  },
  methods: {
    placeTab() { placeIndicator(this.$refs.tabs, 'button.active'); },
    async loadSummary() {
      try {
        const d = await exploreData(this.$pb, { part: 'summary' });
        this.total = d.total;
        this.topRatedReports = d.top;
        this.recent = d.recent.items;
        this.recentMore = d.recent.more;
        this.cityRows = d.cities;
        this.countryRows = d.countries;
      } catch (error) {
        console.error(error);
      }
    },
    async loadMoreRecent() {
      this.busy = true;
      try {
        const d = await exploreData(this.$pb, { part: 'recent', page: this.recentPage + 1 });
        this.recentPage = d.page;
        this.recent = this.recent.concat(d.items);
        this.recentMore = d.more;
      } catch (error) { console.error(error); }
      this.busy = false;
    },
    async loadPoints() {
      if (this.pointsLoaded) return;
      try {
        this.points = (await exploreData(this.$pb, { part: 'points' })).items;
      } catch (error) { console.error(error); }
      this.pointsLoaded = true;
    },
    async loadDrill(kind, name, page = 1) {
      this.busy = true;
      try {
        const d = await exploreData(this.$pb, { part: kind, name, page });
        this.drillReports = page === 1 ? d.items : this.drillReports.concat(d.items);
        this.drillPage = d.page;
        this.drillMore = d.more;
      } catch (error) { console.error(error); }
      this.busy = false;
    },
    loadMoreDrill() {
      if (this.selectedCity) this.loadDrill('city', this.selectedCity, this.drillPage + 1);
      else if (this.selectedCountry) this.loadDrill('country', this.selectedCountry, this.drillPage + 1);
    },
    filterRows(rows) {
      const q = this.query.toLowerCase();
      return q ? rows.filter(r => r.name.toLowerCase().includes(q)) : rows;
    },
    mixSegments(row) {
      return [[1, 'low'], [2, 'medium'], [3, 'high']].map(([k, level]) => ({ level, n: row.mix[k] })).filter(x => x.n > 0);
    },
    mixLabel(row) {
      return `${this.$t('common.severity.low')}: ${row.mix[1]}, ${this.$t('common.severity.medium')}: ${row.mix[2]}, ${this.$t('common.severity.high')}: ${row.mix[3]}`;
    },
    // One sentence for screen readers instead of the decorative bar and legend
    kpiLabel(row) {
      return this.$t('explore.kpiLabel', {
        name: row.name, count: row.count,
        low: row.mix[1], medium: row.mix[2], high: row.mix[3],
        label: row.topLabel ? this.$label(row.topLabel) : '-',
      }) + '.';
    },
    setActiveTab(tab) {
      this.activeTab = tab;
      this.query = '';
      this.$router.replace({ query: { tab } });
    },

    clearSelection() {
      this.selectedCity = null;
      this.selectedCountry = null;
    },
    selectCity(city) {
      this.selectedCity = city;
      this.drillReports = [];
      this.loadDrill('city', city);
    },
    selectCountry(country) {
      this.selectedCountry = country;
      this.drillReports = [];
      this.loadDrill('country', country);
    },

  },
};
</script>
<style scoped>
.load-more { display: block; margin: 16px auto 0; }
.map-legend { margin: 8px 0 0; font-size: 13px; color: var(--color-secondary); display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.map-legend .d { width: 10px; height: 10px; border-radius: 50%; display: inline-block; }
.map-legend .d.sev1 { background: var(--color-severity-low); }
.map-legend .d.sev2 { background: var(--color-severity-medium); }
.map-legend .d.sev3 { background: var(--color-severity-high); }

h3 {
  margin: 0;
  font-size: 16px;
}
/* Segmented control, same as the Tabs component in Figma */
.tabs {
  position: relative;
  display: flex;
  gap: 4px;
  padding: 4px;
  margin: 0 auto;
  max-width: 420px;
  width: 100%;
  box-sizing: border-box;
  background: var(--color-subtle);
  border-radius: var(--radius-pill);
}

.tabs button {
  flex: 1;
  min-height: 44px;
  background: none;
  border: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 15px;
  font-weight: 500;
  padding: 10px 12px;
  border-radius: var(--radius-pill);
  color: var(--color-secondary);
  position: relative;
  z-index: 1;
  transition: color 0.2s ease;
}

/* The mint pill slides to the active tab. Until it is placed, the active tab paints it itself. */
.tabs::before {
  content: '';
  position: absolute;
  top: 4px;
  bottom: 4px;
  left: 0;
  width: var(--ind-w, 0);
  transform: translateX(var(--ind-x, 0));
  opacity: var(--ind-o, 0);
  background: var(--color-primary); /* mint fill with dark ink; selection also shows in bold text, no border */
  border-radius: var(--radius-pill);
  pointer-events: none;
}
.tabs.slide-ready::before {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease;
}

.dark-mode .tabs button {
  color: var(--color-text-light);
}

.tabs button.active {
  color: var(--color-on-primary);
  font-weight: 600;
}
.tabs:not(.slide-ready) button.active {
  background-color: var(--color-primary);
}

@media (prefers-reduced-motion: reduce) {
  .tabs.slide-ready::before { transition: none; }
}
@media (forced-colors: active) {
  .tabs::before { border: 2px solid Highlight; box-sizing: border-box; }
  .tabs button.active { color: HighlightText; }
}

@keyframes fade-in {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}


.back-button {
  display: flex;
  align-items: center;
  margin-top: 16px;
  cursor: pointer;
  color: var(--color-primary-text);
  font-weight: bold;
  transition: all 0.3s ease;
  background-color: transparent;
  border: none;
  outline: none;
  font-size: 20px;
}

.back-button:hover {
  transform: translateX(-4px);
}

.back-button i {
  margin-right: 8px;
}

.city-details {
  margin-top: 16px;
}

.city-details h3 {
  margin-bottom: 8px;
}



/* Search */
.search { position: relative; }
.search i { position: absolute; left: 16px; top: 50%; transform: translateY(-50%); color: var(--color-secondary); pointer-events: none; }
.search input { width: 100%; box-sizing: border-box; min-height: var(--touch-min); padding: 0 16px 0 44px; border: 1px solid var(--color-border-input); border-radius: var(--radius-pill); background: var(--color-surface); color: inherit; font: inherit; font-size: 16px; }
.search input:focus-visible { outline: 2px solid var(--color-primary-text); outline-offset: 2px; }
.empty { text-align: center; color: var(--color-secondary); margin: 24px 0; }

/* City: stat tiles + ranked rows */
.stats { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 16px; }
.stat { display: flex; flex-direction: column; gap: 2px; padding: 16px; background: var(--color-subtle); border-radius: var(--radius-lg); }
.stat-value { font-size: 28px; font-weight: 600; line-height: 1.1; }
.stat-label { font-size: 13px; color: var(--color-secondary); }
.dark-mode .stat-label { color: var(--color-text-light); }
.rank-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 8px; }
.rank-row { width: 100%; display: flex; align-items: center; gap: 12px; min-height: var(--touch-min); padding: 14px 16px; text-align: left; font: inherit; color: inherit; background: var(--color-surface); border: 1px solid var(--color-border); border-radius: var(--radius-lg); cursor: pointer; transition: border-color .2s ease, box-shadow .2s ease; }
.rank-row:hover, .rank-row:focus-visible { border-color: var(--color-primary); box-shadow: var(--shadow-card-hover); }
.rank { font-family: var(--font-mono); font-size: 13px; color: var(--color-secondary); width: 22px; }
.dark-mode .rank { color: var(--color-text-light); }
.rank-main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
.rank-name { font-weight: 600; font-size: 16px; }
.rank-sub { font-size: 13px; color: var(--color-secondary); }
.dark-mode .rank-sub { color: var(--color-text-light); }
.bar { display: block; height: 6px; border-radius: var(--radius-pill); background: var(--color-subtle); overflow: hidden; }
.bar-fill { display: block; height: 100%; border-radius: var(--radius-pill); background: var(--color-primary); }
.rank-count { font-family: var(--font-mono); font-weight: 500; font-size: 18px; }
.chevron { font-size: 12px; color: var(--color-secondary); }

/* Country: featured tile + grid */
/* Featured: mint tint, no border. Number in normal text colour (>= 11:1), bar and legend on a white panel */
.featured { width: 100%; display: block; padding: 0; margin-bottom: 12px; text-align: left; font: inherit; background: var(--color-subtle); border: none; border-radius: var(--radius-xl); cursor: pointer; }
.featured-card { display: flex; flex-direction: column; align-items: flex-start; gap: 6px; padding: 20px; color: var(--color-text); }
.dark-mode .featured-card { color: var(--color-text-light); }
.featured-card .kpi-viz { padding: 10px 12px; margin: 4px 0; background: var(--color-surface); border-radius: var(--radius-md); box-sizing: border-box; }
.featured-card .issue-tag { background: var(--color-surface); }
.eyebrow { color: var(--color-primary-text); font-family: var(--font-mono); font-size: 12px; text-transform: uppercase; letter-spacing: .06em; }
.featured-count { font-family: var(--font-mono); font-size: 44px; font-weight: 500; line-height: 1; }
.featured-name { font-size: 20px; font-weight: 600; }
.tile-grid { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.tile { width: 100%; height: 100%; display: flex; flex-direction: column; align-items: flex-start; gap: 6px; padding: 16px; text-align: left; font: inherit; color: inherit; background: var(--color-surface); border: 1px solid var(--color-border); border-radius: var(--radius-lg); cursor: pointer; transition: border-color .2s ease, box-shadow .2s ease; }
.tile:hover, .tile:focus-visible { border-color: var(--color-primary); box-shadow: var(--shadow-card-hover); }
.tile-count { font-family: var(--font-mono); font-size: 28px; font-weight: 500; line-height: 1; }
.tile-name { font-weight: 600; }
.mix { display: flex; width: 100%; height: 6px; gap: 2px; border-radius: var(--radius-pill); overflow: hidden; }
.mix-seg { min-width: 4px; flex-basis: 0; }
.mix-low { background: var(--color-viz-severity-low); }
.mix-medium { background: var(--color-viz-severity-medium); }
.mix-high { background: var(--color-viz-severity-high); }
.issue-tag { font-size: 12px; padding: 4px 10px; border-radius: var(--radius-pill); background: var(--color-subtle); color: var(--color-text); }
.dark-mode .issue-tag { color: var(--color-text-light); }
.kpi-head { display: flex; align-items: baseline; gap: 6px; }
.kpi-unit { font-size: 13px; color: var(--color-secondary); }
.dark-mode .kpi-unit { color: var(--color-text-light); }
/* Severity bar sits on a white panel; symbols + numbers carry the meaning, never colour alone */
.kpi-viz { display: flex; flex-direction: column; gap: 6px; width: 100%; }
.mix-legend { display: flex; flex-wrap: wrap; gap: 4px 12px; font-family: var(--font-mono); font-size: 13px; }
.mix-key { display: inline-flex; align-items: center; gap: 4px; }
.sym { width: 10px; height: 10px; flex: none; }
.sym-low { border-radius: 50%; background: var(--color-viz-severity-low); }
.sym-medium { background: var(--color-viz-severity-medium); clip-path: polygon(50% 0, 100% 100%, 0 100%); }
.sym-high { background: var(--color-viz-severity-high); }
@media (forced-colors: active) { .mix-seg, .sym { background: CanvasText; forced-color-adjust: none; } .mix { gap: 2px; } }
.featured:focus-visible, .tile:focus-visible, .rank-row:focus-visible { outline: 2px solid var(--color-primary-text); outline-offset: 2px; }

@media (max-width: 767px) {

  .tabs {
    max-width: none;
  }

}

</style>
